// app/learn/path/[slug]/flashcards.tsx
// =============================================================================
// Kursi Learn — Flashcards with Spaced Repetition (SM-2)
// =============================================================================

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Animated,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { router, useLocalSearchParams } from 'expo-router';
import { theme } from '@/constants/theme';
import { LEARN_LAUNCH_CONFIG } from '@/constants/learnLaunchConfig';
import { RuntimeAsyncState } from '@/features/unified-learning/runtime/components/RuntimeAsyncState';

// ── Design tokens ─────────────────────────────────────────────────────────────

const BG       = '#0B0B14';
const SURFACE  = '#16142A';
const SURFACE2 = '#221F3D';
const SURFACE3 = '#2D2B50';
const TEXT     = '#F0EFF8';
const TEXT_MED = '#9896B4';
const TEXT_DIM = '#5E5C78';
const PRIMARY  = theme.colors.primary;
const GREEN    = '#10B981';
const RED      = '#EF4444';
const AMBER    = '#F59E0B';

const SUPABASE_URL      = process.env.EXPO_PUBLIC_SUPABASE_URL      ?? '';
const SUPABASE_ANON_KEY = process.env.EXPO_PUBLIC_SUPABASE_ANON_KEY ?? '';

// ── Types ─────────────────────────────────────────────────────────────────────

type Flashcard = {
  id: string;
  front: string;
  back: string;
  hint: string | null;
  difficulty: number;
  is_due: boolean;
  is_new: boolean;
  review: { interval_days: number; repetitions: number; ease_factor: number } | null;
};

type SessionMode = 'all' | 'due' | 'weak';

// ── Rating configs ─────────────────────────────────────────────────────────────

const RATINGS = [
  { value: 0, label: 'לא ידעתי',    color: RED,    icon: '✗' },
  { value: 2, label: 'קשה מאוד',   color: AMBER,  icon: '△' },
  { value: 3, label: 'ידעתי',       color: GREEN,  icon: '✓' },
  { value: 5, label: 'בקלות',       color: '#60A5FA', icon: '★' },
];

// ── Main component ─────────────────────────────────────────────────────────────

export default function FlashcardsScreen() {
  const params  = useLocalSearchParams<{ slug: string; uid: string; worldId?: string }>();
  const slug    = typeof params.slug    === 'string' ? params.slug    : '';
  const userId  = typeof params.uid     === 'string' ? params.uid     : '';
  const worldId = typeof params.worldId === 'string' ? params.worldId : '';

  const [phase, setPhase]     = useState<'loading' | 'generating' | 'idle' | 'session' | 'done'>('loading');
  const [cards, setCards]     = useState<Flashcard[]>([]);
  const [sessionCards, setSessionCards] = useState<Flashcard[]>([]);
  const [cardIndex, setCardIndex] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const [showHint, setShowHint] = useState(false);
  const [dueCount, setDueCount] = useState(0);
  const [sessionMode, setSessionMode] = useState<SessionMode>('due');
  const [sessionResults, setSessionResults] = useState<{ id: string; rating: number }[]>([]);

  const flipAnim = useRef(new Animated.Value(0)).current;
  const fadeAnim = useRef(new Animated.Value(1)).current;

  const loadCards = useCallback(async () => {
    setPhase('loading');
    try {
      const res = await fetch(`${SUPABASE_URL}/functions/v1/kp-flashcards`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${SUPABASE_ANON_KEY}`, 'apikey': SUPABASE_ANON_KEY },
        body:    JSON.stringify({ action: 'get', world_id: worldId, user_id: userId }),
      });
      const data = await res.json();

      if (data.flashcards?.length === 0) {
        // Auto-generate
        setPhase('generating');
        const genRes = await fetch(`${SUPABASE_URL}/functions/v1/kp-flashcards`, {
          method:  'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${SUPABASE_ANON_KEY}`, 'apikey': SUPABASE_ANON_KEY },
          body:    JSON.stringify({ action: 'generate', world_id: worldId, user_id: userId, slug }),
        });
        await genRes.json();
        // Reload
        return loadCards();
      }

      setCards(data.flashcards ?? []);
      setDueCount(data.due_count ?? 0);
      setPhase('idle');
    } catch {
      setPhase('idle');
    }
  }, [worldId, userId, slug]);

  useEffect(() => { if (worldId && userId) loadCards(); }, [worldId, userId, loadCards]);

  const startSession = (mode: SessionMode) => {
    setSessionMode(mode);
    let pool: Flashcard[];
    if (mode === 'due')  pool = cards.filter((c) => c.is_due);
    else if (mode === 'weak') pool = cards.filter((c) => c.review && c.review.ease_factor < 2.0);
    else                 pool = [...cards];

    if (pool.length === 0) pool = [...cards];
    pool = pool.sort(() => Math.random() - 0.5).slice(0, 20);

    setSessionCards(pool);
    setCardIndex(0);
    setFlipped(false);
    setShowHint(false);
    setSessionResults([]);
    setPhase('session');
  };

  const flipCard = () => {
    if (flipped) return;
    Animated.timing(flipAnim, {
      toValue: 1, duration: 300, useNativeDriver: Platform.OS !== 'web',
    }).start(() => setFlipped(true));
  };

  const rateCard = useCallback(async (rating: number) => {
    const card = sessionCards[cardIndex];
    if (!card) return;

    const newResults = [...sessionResults, { id: card.id, rating }];
    setSessionResults(newResults);

    // Submit to API
    fetch(`${SUPABASE_URL}/functions/v1/kp-flashcards`, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${SUPABASE_ANON_KEY}`, 'apikey': SUPABASE_ANON_KEY },
      body:    JSON.stringify({
        action:       'review',
        flashcard_id: card.id,
        user_id:      userId,
        world_id:     worldId,
        rating,
      }),
    }).catch(() => {});

    // Animate to next card
    Animated.timing(fadeAnim, { toValue: 0, duration: 150, useNativeDriver: true }).start(() => {
      const next = cardIndex + 1;
      if (next >= sessionCards.length) {
        setPhase('done');
      } else {
        setCardIndex(next);
        setFlipped(false);
        setShowHint(false);
        flipAnim.setValue(0);
        Animated.timing(fadeAnim, { toValue: 1, duration: 200, useNativeDriver: true }).start();
      }
    });
  }, [cardIndex, sessionCards, sessionResults, userId, worldId, flipAnim, fadeAnim]);

  if (!LEARN_LAUNCH_CONFIG.advancedTools.flashcards) {
    return (
      <RuntimeAsyncState
        state="degraded"
        title="כרטיסיות זמינות בהמשך"
        description="בטא זו מתמקדת כרגע בלולאת הליבה: שיעור → תרגול → סקירה → מבחן. נפתח כרטיסיות מחדש אחרי חיזוק אמינות."
        actionLabel="חזרה לבית המסלול"
        onAction={() => {
          if (router.canGoBack()) router.back();
          else router.replace(slug ? `/learn/path/${slug}/home` as any : '/learn' as any);
        }}
      />
    );
  }

  // ── Loading ───────────────────────────────────────────────────────────────

  if (phase === 'loading' || phase === 'generating') {
    return (
      <View style={s.centered}>
        <ActivityIndicator color={PRIMARY} size="large" />
        <Text style={s.loadingText}>{phase === 'generating' ? 'יוצר כרטיסיות...' : 'טוען...'}</Text>
      </View>
    );
  }

  // ── Done ──────────────────────────────────────────────────────────────────

  if (phase === 'done') {
    const correct = sessionResults.filter((r) => r.rating >= 3).length;
    const total   = sessionResults.length;
    const pct     = total > 0 ? Math.round((correct / total) * 100) : 0;

    return (
      <View style={s.root}>
        <View style={s.header}>
          <Pressable onPress={() => router.back()} style={s.backBtn}>
            <Text style={s.backText}>‹ חזרה</Text>
          </Pressable>
          <Text style={s.headerTitle}>כרטיסיות</Text>
          <View style={{ width: 64 }} />
        </View>
        <View style={s.doneContainer}>
          <Text style={s.doneEmoji}>{pct >= 80 ? '✓' : pct >= 60 ? '◎' : '○'}</Text>
          <Text style={s.doneTitle}>סשן הושלם!</Text>
          <Text style={s.doneScore}>{correct}/{total} נכונות</Text>
          <View style={s.doneBar}>
            <View style={[s.doneFill, { width: `${pct}%` as any, backgroundColor: pct >= 70 ? GREEN : AMBER }]} />
          </View>
          <Text style={s.donePct}>{pct}%</Text>
          <View style={s.doneActions}>
            <Pressable style={[s.doneBtn, { backgroundColor: PRIMARY }]} onPress={() => startSession(sessionMode)}>
              <Text style={s.doneBtnText}>חזור שוב</Text>
            </Pressable>
            <Pressable style={[s.doneBtn, { backgroundColor: SURFACE2, borderWidth: 1, borderColor: SURFACE3 }]}
              onPress={() => { loadCards(); setPhase('idle'); }}>
              <Text style={[s.doneBtnText, { color: TEXT_MED }]}>חזור לתפריט</Text>
            </Pressable>
          </View>
        </View>
      </View>
    );
  }

  // ── Session ───────────────────────────────────────────────────────────────

  if (phase === 'session') {
    const card = sessionCards[cardIndex];
    const progress = (cardIndex / sessionCards.length) * 100;

    const frontInterp = flipAnim.interpolate({ inputRange: [0, 1], outputRange: ['0deg', '90deg'] });
    const cardStyle = Platform.OS !== 'web'
      ? [s.card, { transform: [{ rotateY: frontInterp }] }]
      : s.card;

    return (
      <View style={s.root}>
        <View style={s.header}>
          <Pressable onPress={() => { setPhase('idle'); loadCards(); }} style={s.backBtn}>
            <Text style={s.backText}>✕ סיים</Text>
          </Pressable>
          <Text style={s.headerTitle}>
            {cardIndex + 1} / {sessionCards.length}
          </Text>
          <View style={{ width: 64 }} />
        </View>

        {/* Progress bar */}
        <View style={s.progressTrack}>
          <View style={[s.progressFill, { width: `${progress}%` as any }]} />
        </View>

        <Animated.View style={{ flex: 1, opacity: fadeAnim, padding: 20, justifyContent: 'center' }}>
          {/* Card */}
          <Pressable
            style={cardStyle as any}
            onPress={flipCard}
            accessibilityRole="button"
            accessibilityLabel={flipped ? 'תשובה מוצגת' : 'לחץ לגילוי התשובה'}
          >
            <View style={s.cardLabel}>
              <Text style={s.cardLabelText}>{flipped ? 'תשובה' : 'שאלה'}</Text>
            </View>

            <Text style={flipped ? s.cardBack : s.cardFront}>
              {flipped ? card.back : card.front}
            </Text>

            {!flipped && (
              <>
                {card.hint && !showHint && (
                  <Pressable style={s.hintBtn} onPress={(e) => { e.stopPropagation?.(); setShowHint(true); }}>
                    <Text style={s.hintBtnText}>הצג רמז</Text>
                  </Pressable>
                )}
                {showHint && card.hint && (
                  <View style={s.hintBox}>
                    <Text style={s.hintText}>{card.hint}</Text>
                  </View>
                )}
                <Text style={s.tapPrompt}>לחץ לגילוי התשובה</Text>
              </>
            )}
          </Pressable>

          {/* Rating buttons */}
          {flipped && (
            <View style={s.ratingRow}>
              {RATINGS.map((r) => (
                <Pressable
                  key={r.value}
                  style={[s.ratingBtn, { borderColor: r.color + '44', backgroundColor: r.color + '14' }]}
                  onPress={() => rateCard(r.value)}
                  accessibilityRole="button"
                  accessibilityLabel={r.label}
                >
                  <Text style={[s.ratingIcon, { color: r.color }]}>{r.icon}</Text>
                  <Text style={[s.ratingLabel, { color: r.color }]}>{r.label}</Text>
                </Pressable>
              ))}
            </View>
          )}
        </Animated.View>
      </View>
    );
  }

  // ── Idle / home ───────────────────────────────────────────────────────────

  const totalCards = cards.length;
  const newCards   = cards.filter((c) => c.is_new).length;
  const weakCards  = cards.filter((c) => c.review && c.review.ease_factor < 2.0).length;

  return (
    <View style={s.root}>
      <View style={s.header}>
        <Pressable onPress={() => router.back()} style={s.backBtn}>
          <Text style={s.backText}>‹ חזרה</Text>
        </Pressable>
        <Text style={s.headerTitle}>כרטיסיות</Text>
        <View style={{ width: 64 }} />
      </View>

      <ScrollView style={{ flex: 1 }} contentContainerStyle={s.idleContent} showsVerticalScrollIndicator={false}>
        {/* Hero stats */}
        <View style={s.statsGrid}>
          <StatCard icon="◧" value={String(totalCards)} label="כרטיסיות" color={PRIMARY} />
          <StatCard icon="◷" value={String(dueCount)}   label="לחזרה"    color={dueCount > 0 ? AMBER : GREEN} />
          <StatCard icon="+" value={String(newCards)}   label="חדשות"    color={GREEN} />
          <StatCard icon="!" value={String(weakCards)}  label="חלשות"    color={RED} />
        </View>

        {/* Session modes */}
        <Text style={s.sectionTitle}>בחר סשן</Text>

        <SessionOption
          icon="⏰"
          title="כרטיסיות לחזרה"
          subtitle={dueCount > 0 ? `${dueCount} כרטיסיות מחכות לחזרה` : 'כל הכרטיסיות עדכניות'}
          color={AMBER}
          count={dueCount}
          onPress={() => startSession('due')}
          disabled={dueCount === 0}
        />
        <SessionOption
          icon="⚠️"
          title="כרטיסיות חלשות"
          subtitle="התמקד בנושאים שקשה לך"
          color={RED}
          count={weakCards}
          onPress={() => startSession('weak')}
          disabled={weakCards === 0}
        />
        <SessionOption
          icon="◧"
          title="כל הכרטיסיות"
          subtitle={`סשן מלא עם כל ${totalCards} הכרטיסיות`}
          color={PRIMARY}
          count={totalCards}
          onPress={() => startSession('all')}
          disabled={totalCards === 0}
        />

        {/* Refresh button */}
        <Pressable style={s.refreshBtn} onPress={loadCards}>
          <Text style={s.refreshBtnText}>🔄 רענן כרטיסיות</Text>
        </Pressable>

        <View style={{ height: 32 }} />
      </ScrollView>
    </View>
  );
}

// ── Sub-components ─────────────────────────────────────────────────────────────

function StatCard({ icon, value, label, color }: { icon: string; value: string; label: string; color: string }) {
  return (
    <View style={[sc.statCard, { borderColor: color + '2A' }]}>
      <Text style={sc.statIcon}>{icon}</Text>
      <Text style={[sc.statValue, { color }]}>{value}</Text>
      <Text style={sc.statLabel}>{label}</Text>
    </View>
  );
}

function SessionOption({ icon, title, subtitle, color, count, onPress, disabled }: {
  icon: string; title: string; subtitle: string; color: string;
  count: number; onPress: () => void; disabled: boolean;
}) {
  return (
    <Pressable
      style={[sc.option, disabled && sc.optionDisabled, { borderColor: disabled ? SURFACE3 : color + '3A' }]}
      onPress={onPress}
      disabled={disabled}
      accessibilityRole="button"
    >
      <Text style={sc.optionIcon}>{icon}</Text>
      <View style={sc.optionText}>
        <Text style={[sc.optionTitle, disabled && { opacity: 0.4 }]}>{title}</Text>
        <Text style={sc.optionSub}>{subtitle}</Text>
      </View>
      <View style={[sc.optionBadge, { backgroundColor: color + '14', borderColor: color + '33' }]}>
        <Text style={[sc.optionCount, { color }]}>{count}</Text>
      </View>
    </Pressable>
  );
}

const sc = StyleSheet.create({
  statCard: {
    flex: 1, backgroundColor: SURFACE, borderRadius: 12, borderWidth: 1,
    padding: 12, alignItems: 'center', gap: 4,
  },
  statIcon:  { fontSize: 20 },
  statValue: { fontFamily: theme.fonts.bold, fontSize: 18 },
  statLabel: { fontFamily: theme.fonts.regular, fontSize: 11, color: TEXT_DIM, textAlign: 'center' },

  option: {
    backgroundColor: SURFACE, borderRadius: 16, borderWidth: 1,
    padding: 16, flexDirection: 'row-reverse', alignItems: 'center', gap: 12,
  },
  optionDisabled: { opacity: 0.5 },
  optionIcon:  { fontSize: 24, width: 32, textAlign: 'center' },
  optionText:  { flex: 1, gap: 3 },
  optionTitle: { fontFamily: theme.fonts.bold, fontSize: 15, color: TEXT, textAlign: 'right' },
  optionSub:   { fontFamily: theme.fonts.regular, fontSize: 12, color: TEXT_MED, textAlign: 'right' },
  optionBadge: {
    borderRadius: 99, borderWidth: 1, paddingHorizontal: 10, paddingVertical: 4,
  },
  optionCount: { fontFamily: theme.fonts.bold, fontSize: 14 },
});

// ── Styles ─────────────────────────────────────────────────────────────────────

const s = StyleSheet.create({
  root:    { flex: 1, backgroundColor: BG },
  centered: {
    flex: 1, backgroundColor: BG,
    alignItems: 'center', justifyContent: 'center', gap: 14,
  },
  loadingText: { color: TEXT_MED, fontFamily: theme.fonts.regular, fontSize: 14 },

  header: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingTop: Platform.OS === 'web' ? 20 : 16,
    paddingBottom: 14,
    borderBottomWidth: 1,
    borderBottomColor: SURFACE3,
  },
  backBtn:     { paddingHorizontal: 8, paddingVertical: 6, minWidth: 64 },
  backText:    { color: PRIMARY, fontFamily: theme.fonts.medium, fontSize: 15 },
  headerTitle: {
    flex: 1, color: TEXT, fontFamily: theme.fonts.bold,
    fontSize: 17, textAlign: 'center',
  },

  progressTrack: { height: 3, backgroundColor: SURFACE2 },
  progressFill:  { height: 3, backgroundColor: PRIMARY, borderRadius: 3 },

  idleContent: {
    paddingHorizontal: 16,
    paddingTop: 20,
    maxWidth: 600,
    alignSelf: 'center' as any,
    width: '100%',
    gap: 12,
  },
  statsGrid:    { flexDirection: 'row-reverse', gap: 8 },
  sectionTitle: {
    fontFamily: theme.fonts.bold, fontSize: 12, color: TEXT_DIM,
    textAlign: 'right', letterSpacing: 0.6, textTransform: 'uppercase' as any,
    paddingTop: 8,
  },

  refreshBtn: {
    borderRadius: 12, borderWidth: 1, borderStyle: 'dashed' as any,
    borderColor: PRIMARY + '33', paddingVertical: 12,
    alignItems: 'center', marginTop: 4,
  },
  refreshBtnText: { color: PRIMARY, fontFamily: theme.fonts.medium, fontSize: 13 },

  // Card
  card: {
    backgroundColor: SURFACE,
    borderRadius: 24,
    borderWidth: 1,
    borderColor: SURFACE3,
    padding: 28,
    minHeight: 240,
    justifyContent: 'center',
    alignItems: 'center',
    gap: 16,
    ...(Platform.OS === 'web' ? ({ boxShadow: '0 8px 40px rgba(0,0,0,0.5)' } as any) : {}),
  },
  cardLabel: {
    position: 'absolute', top: 14, right: 18,
    backgroundColor: SURFACE2, borderRadius: 99, paddingHorizontal: 10, paddingVertical: 3,
    borderWidth: 1, borderColor: SURFACE3,
  },
  cardLabelText: { fontFamily: theme.fonts.bold, fontSize: 10, color: TEXT_DIM, letterSpacing: 0.5 },
  cardFront: {
    fontFamily: theme.fonts.bold, fontSize: 20, color: TEXT,
    textAlign: 'center', lineHeight: 30,
  },
  cardBack: {
    fontFamily: theme.fonts.regular, fontSize: 16, color: TEXT,
    textAlign: 'center', lineHeight: 26,
  },
  hintBtn: {
    borderRadius: 99, borderWidth: 1, borderColor: AMBER + '44',
    paddingHorizontal: 14, paddingVertical: 6, backgroundColor: AMBER + '0F',
  },
  hintBtnText: { fontFamily: theme.fonts.medium, fontSize: 13, color: AMBER },
  hintBox:  {
    backgroundColor: AMBER + '0F', borderRadius: 12, borderWidth: 1, borderColor: AMBER + '2A',
    paddingHorizontal: 14, paddingVertical: 10,
  },
  hintText: { fontFamily: theme.fonts.regular, fontSize: 13, color: AMBER, textAlign: 'center' },
  tapPrompt: { fontFamily: theme.fonts.regular, fontSize: 12, color: TEXT_DIM, marginTop: 8 },

  ratingRow: {
    flexDirection: 'row-reverse',
    gap: 8,
    marginTop: 16,
    justifyContent: 'center',
    flexWrap: 'wrap' as any,
  },
  ratingBtn: {
    borderRadius: 14, borderWidth: 1,
    paddingHorizontal: 16, paddingVertical: 10,
    alignItems: 'center', gap: 4, minWidth: 76,
  },
  ratingIcon:  { fontSize: 18 },
  ratingLabel: { fontFamily: theme.fonts.bold, fontSize: 11, textAlign: 'center' },

  // Done screen
  doneContainer: {
    flex: 1, alignItems: 'center', justifyContent: 'center',
    paddingHorizontal: 24, gap: 14,
  },
  doneEmoji: { fontSize: 60 },
  doneTitle: { fontFamily: theme.fonts.bold, fontSize: 24, color: TEXT },
  doneScore: { fontFamily: theme.fonts.regular, fontSize: 16, color: TEXT_MED },
  doneBar:   { width: '100%', height: 8, backgroundColor: SURFACE2, borderRadius: 4, overflow: 'hidden' },
  doneFill:  { height: 8, borderRadius: 4 },
  donePct:   { fontFamily: theme.fonts.bold, fontSize: 22, color: GREEN },
  doneActions: { flexDirection: 'row-reverse', gap: 10, marginTop: 8 },
  doneBtn: {
    borderRadius: 13, paddingHorizontal: 24, paddingVertical: 13,
  },
  doneBtnText: { fontFamily: theme.fonts.bold, fontSize: 14, color: '#fff' },
});
